import React from 'react';
import classNames from 'classnames';

import LayoutComposer from '../LayoutComposer';
import LayoutWrapperAccountSettingsSideNav from './LayoutWrapperAccountSettingsSideNav';

import css from './LayoutSideNavigation.module.css';

/**
 * Commonly used layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string?} props.mainColumnClassName add more style rules in addition to css.main
 * @param {string?} props.sideNavClassName add more style rules in addition to css.sideNav
 * @param {ReactNode} props.children
 * @param {ReactNode} props.topbar
 * @param {ReactNode?} props.sideNav
 * @param {ReactNode?} props.footer
 * @param {boolean?} props.useAccountSettingsNav
 * @param {Object?} props.accountHeader "Editorial Quiet" account-family page
 *   opener: { eyebrow, title, sub? } nodes. When given together with
 *   useAccountSettingsNav, the layout switches to the account variant —
 *   header / side-nav / content on a desktop grid, with the nav rendered as
 *   the reference's pill row between the header and the content below 1024px.
 *   Consumers that don't pass it (e.g. InboxPage) get the stock layout.
 * @param {string?} props.currentPage
 * @returns {JSX.Element} LayoutComposer that expects children to be a function.
 */
const LayoutSideNavigation = props => {
  const {
    className,
    rootClassName,
    containerClassName,
    mainColumnClassName,
    sideNavClassName,
    children,
    topbar: topbarContent,
    footer: footerContent,
    sideNav: sideNavContent,
    useAccountSettingsNav,
    accountSettingsNavProps,
    accountHeader,
    intl,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const containerClasses = containerClassName || css.container;
  const isAccountVariant = useAccountSettingsNav && !!accountHeader;

  // TODO: since responsiveAreas are still experimental,
  //       we don't separate "aside" through layoutComposer
  const layoutAreas = `
    topbar
    main
    footer
  `;

  const accountSettingsNav = useAccountSettingsNav ? (
    <LayoutWrapperAccountSettingsSideNav
      accountSettingsNavProps={accountSettingsNavProps}
      ariaLabel={intl.formatMessage({
        id: 'LayoutSideNavigation.screenreader.accountNavigation',
      })}
    />
  ) : null;

  return (
    <LayoutComposer areas={layoutAreas} className={classes} {...rest}>
      {layoutProps => {
        const { Topbar, Main, Footer } = layoutProps;
        return (
          <>
            <Topbar as="header" className={css.topbar}>
              {topbarContent}
            </Topbar>
            {isAccountVariant ? (
              <Main as="div" className={css.accountContainer}>
                <div className={css.accountBody}>
                  <header className={css.accountHeading}>
                    <div className={css.accountEyebrow}>{accountHeader.eyebrow}</div>
                    <h1
                      className={classNames(css.accountTitle, {
                        [css.accountTitleNoSub]: !accountHeader.sub,
                      })}
                    >
                      {accountHeader.title}
                    </h1>
                    {accountHeader.sub ? (
                      <p className={css.accountSub}>{accountHeader.sub}</p>
                    ) : null}
                    <div className={css.accountRule} />
                  </header>
                  <div className={css.accountNav}>{accountSettingsNav}</div>
                  <main id="main-content" className={css.accountContent}>
                    {children}
                  </main>
                </div>
              </Main>
            ) : (
              <Main as="div" className={containerClasses}>
                <aside className={classNames(css.sideNav, sideNavClassName)}>
                  {accountSettingsNav}
                  {sideNavContent}
                </aside>
                <main id="main-content" className={classNames(css.main, mainColumnClassName)}>
                  {children}
                </main>
              </Main>
            )}
            <Footer>{footerContent}</Footer>
          </>
        );
      }}
    </LayoutComposer>
  );
};

export default LayoutSideNavigation;
